import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import api, { formatApiError, startModuleCheckout } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";

const STEPS_DEF = [
  { key: "situation", titleKey: "sim.s1t", opts: [["etudiant", "sim.s1o1"], ["emploi", "sim.s1o2"], ["transition", "sim.s1o3"], ["retraite", "sim.s1o4"]] },
  { key: "experience", titleKey: "sim.s2t", opts: [["0", "sim.s2o1"], ["1-3", "sim.s2o2"], ["4-5", "sim.s2o3"], ["5-7", "sim.s2o4"], ["7-12", "sim.s2o5"], ["12+", "sim.s2o6"]] },
  { key: "responsabilite", titleKey: "sim.s3t", opts: [["aucune", "sim.s3o1"], ["equipe", "sim.s3o2"], ["direction", "sim.s3o3"], ["comex", "sim.s3o4"]] },
  { key: "objectif", titleKey: "sim.s4t", opts: [["decouvrir", "sim.s4o1"], ["progresser", "sim.s4o2"], ["diriger", "sim.s4o3"], ["transmettre", "sim.s4o4"]] },
];

export default function Simulateur() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const choose = async (key, value) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (step < STEPS_DEF.length - 1) {
      setStep(step + 1);
    } else {
      setLoading(true);
      try {
        const { data } = await api.post("/orientation", next);
        setResult(data.module);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    }
  };

  const subscribe = async () => {
    try {
      if (result.price > 0) {
        await startModuleCheckout(result.id);
        return;
      }
      await api.post("/subscriptions", { module_id: result.id });
      toast.success(`${t("sim.activate")} ${result.name}`);
      navigate("/espace-client");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const restart = () => { setStep(0); setAnswers({}); setResult(null); };
  const current = STEPS_DEF[step];

  return (
    <div data-testid="simulateur-page" className="bg-paper min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-[72px]">
        <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
          <div className="h-1 bg-linen rounded-full overflow-hidden" data-testid="quiz-progress">
            <motion.div
              className="h-full bg-sage"
              animate={{ width: result ? "100%" : `${(step / STEPS_DEF.length) * 100}%` }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>

          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-32 text-center">
                <p className="font-serif italic text-2xl text-smoke">{t("sim.loading")}</p>
              </motion.div>
            ) : result ? (
              <motion.div key="result" data-testid="quiz-result" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="pt-16">
                <p className="text-xs uppercase tracking-[0.28em] text-sage">{t("sim.resultOver")}</p>
                <div className="mt-8 rounded-3xl bg-ink text-paper p-10 md:p-14">
                  <span className="font-serif text-sm tracking-[0.3em] text-paper/50">{String(result.id).padStart(2, "0")}</span>
                  <h2 className="mt-4 font-serif text-5xl md:text-6xl tracking-tight">{result.name}</h2>
                  <p className="mt-3 text-xs uppercase tracking-[0.18em] text-paper/50">{result.range}</p>
                  <p className="mt-6 text-paper/75 leading-relaxed max-w-lg">{result.tagline}</p>
                  <p className="mt-8 font-serif text-3xl">
                    {result.price_label}
                    {result.price > 0 && <span className="text-sm text-paper/60"> {t("sim.perMonth")}</span>}
                  </p>
                </div>
                <div className="mt-10 flex flex-wrap gap-4">
                  {user ? (
                    <button data-testid="quiz-subscribe-button" onClick={subscribe} className="rounded-full bg-ink text-paper px-8 py-4 text-sm hover:bg-sagedark transition-colors duration-300">
                      {result.price > 0 ? `${t("sim.subscribe")} — ${result.price_label} ${t("sim.perMonth")}` : `${t("sim.activate")} ${result.name}`}
                    </button>
                  ) : (
                    <Link data-testid="quiz-register-cta" to={`/connexion?mode=register&module=${result.id}`} className="rounded-full bg-ink text-paper px-8 py-4 text-sm hover:bg-sagedark transition-colors duration-300">
                      {t("sim.registerCta")}
                    </Link>
                  )}
                  <button data-testid="quiz-restart-button" onClick={restart} className="rounded-full border border-ink/25 px-8 py-4 text-sm hover:border-ink transition-colors duration-300">
                    {t("sim.restart")}
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="pt-14">
                <p className="text-xs uppercase tracking-[0.28em] text-sage">{t("sim.step")} {step + 1} / {STEPS_DEF.length}</p>
                <h1 className="mt-5 font-serif text-3xl md:text-5xl tracking-tight text-ink">{t(current.titleKey)}</h1>
                <div className="mt-10 grid gap-4">
                  {current.opts.map(([value, k]) => {
                    const [label, hint] = t(k);
                    return (
                      <button
                        key={value}
                        data-testid={`quiz-option-${current.key}-${value}`}
                        onClick={() => choose(current.key, value)}
                        className="group flex items-center justify-between gap-4 text-left bg-white rounded-2xl border border-linen px-6 md:px-8 py-5 hover:border-sage hover:shadow-[0_16px_40px_-20px_rgba(26,29,26,0.25)] transition-[border-color,box-shadow] duration-300"
                      >
                        <span>
                          <span className="block text-base md:text-lg text-ink">{label}</span>
                          <span className="block text-sm text-smoke mt-1">{hint}</span>
                        </span>
                        <ArrowRight size={18} strokeWidth={1.5} className="shrink-0 text-smoke group-hover:text-sagedark group-hover:translate-x-1 transition-[transform,color] duration-300" />
                      </button>
                    );
                  })}
                </div>
                {step > 0 && (
                  <button data-testid="quiz-back-button" onClick={() => setStep(step - 1)} className="mt-8 inline-flex items-center gap-2 text-sm text-smoke hover:text-ink transition-colors duration-300">
                    <ArrowLeft size={16} strokeWidth={1.5} /> {t("sim.back")}
                  </button>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
      <Footer />
    </div>
  );
}
